import { roles } from "./roles";

/**
 * Agentic-platform (rxConductor) shared constants: the Tasks-hub sections
 * (refresh cadence, HITL task types) and the access gate used by the sidebar,
 * routes, and patient-page surfaces. One file so they can't drift apart.
 */

/**
 * Background auto-refresh cadence (ms). Each section skips the refresh while
 * its tab is hidden or a HITL modal is open, so it never reloads the table
 * out from under the navigator.
 */
export const AUTO_REFRESH_MS = 60000;

/**
 * The navigator pause-points the Tasks hub can open. Mirrors the platform's
 * `AgenticHitlTaskType` enum. The screenshot-upload operator flow is NOT a HITL
 * pause — it lives in the EMR-tasks tab (its own table/endpoint), not here.
 * Shared so the hub page + the HITL modal reference one definition.
 */
export const HITL_TASK_TYPES = Object.freeze({
  EMR_FILL: "emr_fill",
  CONSENT_DECIDE: "consent_decide",
  // Operator screenshot upload (EMR Tasks tab) — a HITL pause like the two
  // navigator tasks; the run's status is `waiting_hitl` and THIS field says
  // who/where. Not navigator-resumable (no hitl-decision on it).
  EMR_SCREENSHOTS: "emr_screenshots",
  // Navigator reviews + approves an express enrollment (Tasks hub). Its modal
  // mounts the REAL express-enrollment form, so approving both fills the
  // application in FN and resumes the run.
  EE_REVIEW: "ee_review"
});

/**
 * The pipeline step names the platform writes on `agentic_workflow_run_steps`.
 * Mirrors the platform's `AgenticStepName` enum (tm-agentic-platform) — the wire
 * values must stay byte-identical, since the FE matches audit rows by them.
 * Single definition so the audit modal, the Tasks hub, and the dashboard can't
 * drift from each other or from the platform.
 */
export const AGENTIC_STEP_NAMES = Object.freeze({
  EMR_GATHERING: "emr_gathering",
  EE_CONFIG_CHECK: "ee_config_check",
  EE_HUMAN_REVIEW: "ee_human_review",
  EE_SUBMIT: "ee_submit",
  EMR_DATA_PARSED: "emr_data_parsed",
  FINANCIAL_ASSISTANCE_ELIGIBILITY: "financial_assistance_eligibility",
  REMAINING_OOP_CALCULATION: "remaining_oop_calculation",
  CONSENT_STATE_CHECK: "consent_state_check",
  ASSIGN_PATIENT_TO_AGENT: "assign_patient_to_agent",
  UPDATE_PATIENT_STATUS: "update_patient_status",
  /** The run's hypercare wrap-up — what it closed and what it handed back, in
   *  one row. `outcome` is a HYPERCARE_HANDOFF_OUTCOME. */
  HYPERCARE_HANDOFF: "hypercare_handoff",
  SEND_CONSENT: "send_consent",
  CONSENT_HUMAN_REVIEW: "consent_human_review",
  CONSENT_SIGNATURE_WAIT: "consent_signature_wait",
  ENROLLMENT_CANDIDATES: "enrollment_candidates",
  EMR_MANUAL_FILL: "emr_manual_fill"
});

/**
 * Why a recommended program is NOT one the agent can auto-enroll. Mirrors the
 * platform's `EnrollmentSkipReason`, recorded per pap on the
 * `enrollment_candidates` step.
 */
export const ENROLLMENT_SKIP_REASON = Object.freeze({
  PROGRAM_NOT_CONFIGURED: "program_not_configured",
  DRUG_NOT_SUPPORTED: "drug_not_supported",
  HAS_ACTIVE_APPLICATION: "has_active_application"
});

/**
 * `ee_submit` step outcome. Mirrors the platform's `EeSubmissionResult` (and
 * FN's `EE_SUBMISSION_RESULTS`) — a vendor rejection is a business verdict, not
 * a failed request, so both endings are `passed` steps and the OUTCOME is what
 * tells them apart.
 */
export const EE_SUBMISSION_RESULT = Object.freeze({
  SUBMITTED: "submitted",
  SUBMIT_FAILED: "submit_failed"
});

/**
 * `consent_signature_wait` outcomes. The post-send wait is NOT a HITL pause —
 * nobody is asked to decide anything, so it never appears in
 * `NAVIGATOR_PAUSE_TASK_TYPE_BY_STEP` and carries no task type. An open one
 * (`result == null`) is waiting on the PATIENT to sign the link; the window
 * closing is a normal ending, not a failure.
 */
export const CONSENT_SIGNATURE_WAIT_OUTCOME = Object.freeze({
  SIGNED: "signed",
  NOT_SIGNED: "not_signed"
});

/** How the wait learned the consent was signed — `detectedVia` on its payload. */
export const CONSENT_SIGNATURE_DETECTION = Object.freeze({
  SIGNAL: "signal",
  POLL: "poll"
});

/**
 * Step name → the navigator task type whose pause it records. The FE mirror of
 * the platform's `HITL_TASK_STEP_NAME`, narrowed to its
 * `NAVIGATOR_RESUMABLE_HITL_TASK_TYPES` — so `emr_screenshots` is absent by
 * construction: that pause is resumed by the operator's upload, never by a
 * navigator decision, so there is no navigator task to send anyone to.
 *
 * A step of one of these names with `result == null` IS an open navigator
 * pause, and it's the only tree-wide pause signal the FE gets: the latest-run
 * payload carries the ROOT run's status plus every run's steps MERGED, so a
 * pause parked on a child row surfaces here and nowhere else.
 * `step.agenticRunId` names the row that owns it.
 */
export const NAVIGATOR_PAUSE_TASK_TYPE_BY_STEP = Object.freeze({
  [AGENTIC_STEP_NAMES.EMR_MANUAL_FILL]: HITL_TASK_TYPES.EMR_FILL,
  [AGENTIC_STEP_NAMES.CONSENT_HUMAN_REVIEW]: HITL_TASK_TYPES.CONSENT_DECIDE,
  [AGENTIC_STEP_NAMES.EE_HUMAN_REVIEW]: HITL_TASK_TYPES.EE_REVIEW
});

/**
 * `agentic_workflow_run_steps.result` DB enum — a step's technical outcome
 * (`null` while the step is still in progress). Lowercase to match the stored
 * values; a mis-cased lookup would silently read as "skipped" everywhere.
 */
export const AUDIT_STEP_RESULT = Object.freeze({
  PASSED: "passed",
  // ONLY an error — `payload.error` carries the cause. An expiry is TIMED_OUT.
  FAILED: "failed",
  // The step's window closed with no answer: an unanswered navigator task, an
  // operator who never uploaded, a patient who never signed. Neither a failure
  // (nothing broke) nor a pass (nothing was answered). Mirrors
  // AGENTIC_RUN_STATUS.EXPIRED one level up.
  TIMED_OUT: "timed_out",
  // FE-ONLY: the platform never writes this. Synthesized for a step that passed
  // with nothing to show (see `hasNoOopData`) and used as the fallback visual for
  // a result this build doesn't recognize.
  SKIPPED: "skipped"
});

/**
 * Denormalized run status (`agentic_workflow_runs.status`, platform
 * `AgenticRunStatus`). `RUN_STATUS_META` (runStatuses.js) is keyed by these.
 */
export const AGENTIC_RUN_STATUS = Object.freeze({
  RUNNING: "running",
  // A paused run is always `waiting_hitl` — `run.hitlTaskType`
  // (emr_screenshots / emr_fill / consent_decide) says which human.
  WAITING_HITL: "waiting_hitl",
  // Terminal lifecycle only. HOW the run ended is `run.finalOutcome`;
  // display buckets derive from it (runStatuses.js).
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  // A HITL pause ran out of time — nobody acted, so the run handed the patient
  // back and stopped. Its own terminal rather than `failed` so an unanswered
  // task isn't counted (or shown) as a broken agent. Runs that timed out before
  // the platform added this still arrive as `failed`; both must stay handled.
  EXPIRED: "expired"
});

/**
 * A navigator's decision on a HITL pause (`payload.decision`), plus the
 * workflow-written `timeout` when the wait window elapses with no response.
 */
export const REVIEW_DECISION = Object.freeze({
  APPROVE: "approve",
  DECLINE: "decline"
});

/**
 * Patient contact-field identifiers as they appear on step payloads
 * (`availableContactChannels` / `missingContactFields`) and the consent form.
 */
export const CONTACT_FIELD = Object.freeze({
  EMAIL: "email",
  PHONE_NUMBER: "phoneNumber",
  DOB: "dob"
});

/**
 * `financial_assistance_eligibility` step outcome (platform
 * FINANCIAL_ASSISTANCE_REASONS). Every not-eligible variant shares
 * {@link NOT_ELIGIBLE_PREFIX} so the UI can group them with one check.
 */
export const FIN_ASSIST_REASON = Object.freeze({
  ELIGIBLE: "eligible",
  NOT_ELIGIBLE_NO_INSURANCE_DATA: "not_eligible_no_insurance_data",
  NOT_ELIGIBLE_NO_REASONS: "not_eligible_no_reasons",
  NO_ACTIVE_MEDICATIONS: "no_active_medications"
});

/** Shared prefix of every `FIN_ASSIST_REASON` not-eligible variant. */
export const NOT_ELIGIBLE_PREFIX = "not_eligible";

/**
 * `emr_gathering` step outcome (platform EmrGatheringExitMode). OPERATOR_DONE +
 * ALREADY_FRESH are terminal successes; the operator_done_* trio +
 * NO_SCREENSHOTS_PROVIDED are failures; the WAITING_/PROCESSING_ pair are the
 * two in-progress sub-phases.
 */
export const EMR_GATHERING_OUTCOME = Object.freeze({
  OPERATOR_DONE: "operator_done",
  ALREADY_FRESH: "already_fresh",
  OPERATOR_DONE_WRONG_PATIENT: "operator_done_wrong_patient",
  OPERATOR_DONE_EXTRACTION_FAILED: "operator_done_extraction_failed",
  OPERATOR_DONE_NO_UPLOADS: "operator_done_no_uploads",
  NO_SCREENSHOTS_PROVIDED: "no_screenshots_provided",
  GATHERING_ERROR: "gathering_error",
  OPERATOR_DONE_PARTIAL_DATA: "operator_done_partial_data",
  WAITING_FOR_SCREENSHOTS: "waiting_for_screenshots",
  PROCESSING_SCREENSHOTS: "processing_screenshots"
});

/**
 * Consent state (platform `ConsentReason`) carried on the consent step's
 * `outcome` and — pre-decision — on the consent_human_review step's `outcome`.
 */
export const CONSENT_REASON = Object.freeze({
  ELIGIBLE_TO_SEND: "eligible_to_send",
  PATIENT_HAS_ACTIVE_CONSENT: "patient_has_active_consent",
  CONSENT_LINK_IN_PROGRESS: "consent_link_in_progress",
  CONSENT_LINK_EXPIRED: "consent_link_expired",
  CONSENT_LINK_REVOKED: "consent_link_revoked",
  CONSENT_REVOKED: "consent_revoked",
  CONSENT_EXPIRED: "consent_expired",
  CONTACT_INFO_MISSING: "contact_info_missing"
});

/**
 * `send_consent` step outcome — the happy-path SENT, or (when a re-check finds
 * a state shift) one of the {@link CONSENT_REASON} values. A dispatch failure
 * crashes the run (step written FAILED with `payload.error`).
 */
export const SEND_CONSENT_OUTCOME = Object.freeze({
  SENT: "sent"
});

/**
 * `hypercare_handoff` step outcome — what the run left on the board, stated from
 * the NAVIGATOR's side (is there work for me?).
 *
 * Binary on purpose: a partial enrollment is a handback like any other. Whether
 * the agent closed anything on its way is `payload.completedRowIds`, not a third
 * value restating the run's own ENROLLMENT_PARTIAL, which the badge already shows.
 */
export const HYPERCARE_HANDOFF_OUTCOME = Object.freeze({
  /** Every claimed row resolved — nothing came back. */
  COMPLETED: "completed",
  /** Something came back — a navigator has work on this patient. */
  HANDED_BACK: "handed_back"
});

/**
 * Run-level final reason (`agentic_workflow_runs.finalOutcome`, platform
 * RxConductorFinalOutcome) plus the legacy consent codes the audit modal
 * still translates for older rows.
 */
export const RUN_FINAL_OUTCOME = Object.freeze({
  FAILURE: "failure",
  NOT_ELIGIBLE: "not_eligible",
  OOP_MET: "oop_met",
  NO_ACTIVE_MEDICATIONS: "no_active_medications",
  CONSENT_SENT_BY_NAVIGATOR: "consent_sent_by_navigator",
  REVIEW_DECLINED: "review_declined",
  /**
   * Consent was in hand — already on file, or signed inside the wait — but the
   * agent had nothing it could enroll into, so the case goes to a navigator.
   * Superseded `PATIENT_HAS_ACTIVE_CONSENT`, which named the DOOR rather than
   * the ending and so couldn't cover the signed-inside-the-window path.
   */
  CONSENT_OBTAINED_HANDOFF: "consent_obtained_handoff",
  ENROLLED: "enrolled",
  ENROLLMENT_PARTIAL: "enrollment_partial",
  /**
   * A HITL pause holding the run tree elapsed its TTL — nobody acted, so the
   * rows went back to the navigator. Pairs with `AGENTIC_RUN_STATUS.EXPIRED`;
   * WHICH pause ran out isn't a run-level answer (it's on the step timeline).
   */
  HITL_TIMEOUT: "hitl_timeout"
});

/**
 * Roles that can see the agentic-platform surfaces (sidebar entry, the
 * /agentic-platform routes, patient-page runs button). FN's ACL already grants
 * these roles the `agenticplatform` resource server-side — this list is the FE
 * visibility mirror of that.
 */
export const agenticPlatformAllowedRoles = [roles.OPERATION_MANGER, roles.FINANCIAL_NAVIGATOR, roles.INSIGHTS];

/**
 * The agentic-platform access gate: a TMComplete user (TailorMed-employed
 * centralized navigator) whose account ALSO carries one of the allowed roles —
 * both conditions required. Site-level enablement (`tmAgenticPlatformEnabled`)
 * is checked separately by each surface.
 */
export const canAccessAgenticPlatform = (user) =>
  Boolean(user && user.isTMComplete && agenticPlatformAllowedRoles.includes(user.role));
